import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Upload,
  message,
  Popconfirm,
  Tag,
  Image,
  Row,
  Col,
  Empty
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileOutlined
} from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../utils/auth';

const { Option } = Select;

const NoticeAttachments = () => {
  const [attachments, setAttachments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);
  const [uploadForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

  useEffect(() => {
    fetchAttachments();
    fetchCategories();
  }, [filterCategory]);

  const fetchAttachments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory) {
        params.append('category', filterCategory);
      }
      const response = await axios.get(
        `${API_BASE}/notice-attachments?${params.toString()}`,
        { headers: AuthService.getAuthHeaders() }
      );
      if (response.data.success) {
        setAttachments(response.data.data);
      }
    } catch (error) {
      message.error('获取附件列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${API_BASE}/notice-attachments/categories`,
        { headers: AuthService.getAuthHeaders() }
      );
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('获取分类失败');
    }
  };

  const handleUpload = async () => {
    try {
      const values = await uploadForm.validateFields();

      if (!values.file || values.file.length === 0) {
        message.error('请选择文件');
        return;
      }

      const formData = new FormData();
      formData.append('file', values.file[0].originFileObj);
      formData.append('name', values.name);
      if (values.nameEn) formData.append('nameEn', values.nameEn);

      await axios.post(`${API_BASE}/notice-attachments/upload`, formData, {
        headers: {
          ...AuthService.getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });

      message.success('上传成功');
      setUploadModalVisible(false);
      uploadForm.resetFields();
      fetchAttachments();
      fetchCategories();
    } catch (error) {
      message.error('上传失败');
    }
  };

  const handleEdit = (record) => {
    setEditingAttachment(record);
    editForm.setFieldsValue(record);
    setEditModalVisible(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await editForm.validateFields();
      await axios.put(
        `${API_BASE}/notice-attachments/${editingAttachment.id}`,
        values,
        { headers: AuthService.getAuthHeaders() }
      );
      message.success('更新成功');
      setEditModalVisible(false);
      fetchAttachments();
      fetchCategories();
    } catch (error) {
      message.error('更新失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE}/notice-attachments/${id}`, {
        headers: AuthService.getAuthHeaders()
      });
      message.success('删除成功');
      fetchAttachments();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image':
        return <FileImageOutlined style={{ fontSize: 48, color: '#1890ff' }} />;
      case 'pdf':
        return <FilePdfOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />;
      default:
        return <FileOutlined style={{ fontSize: 48, color: '#666' }} />;
    }
  };

  const getFileUrl = (attachment) => {
    if (attachment.signedUrl) {
      return attachment.signedUrl;
    }
    if (attachment.filePath?.startsWith('/uploads/')) {
      return `${API_BASE}${attachment.filePath}`;
    }
    return attachment.filePath;
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="附件管理"
        extra={
          <Space>
            <Select
              placeholder="按分类筛选"
              allowClear
              style={{ width: 150 }}
              value={filterCategory}
              onChange={setFilterCategory}
            >
              {categories.map((cat) => (
                <Option key={cat} value={cat}>
                  {cat}
                </Option>
              ))}
            </Select>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => {
                uploadForm.resetFields();
                setUploadModalVisible(true);
              }}
            >
              上传附件
            </Button>
          </Space>
        }
      >
        <p style={{ color: '#666', marginBottom: 16 }}>
          上传的附件可在「通知书管理」中关联到目的地，用于出团通知书展示，如入境卡填写指南、海关申报说明图片等。
        </p>

        {attachments.length === 0 ? (
          <Empty description="暂无附件" />
        ) : (
          <Row gutter={[16, 16]}>
            {attachments.map((attachment) => (
              <Col key={attachment.id} xs={24} sm={12} md={8} lg={6}>
                <Card
                  hoverable
                  cover={
                    attachment.fileType === 'image' ? (
                      <div
                        style={{
                          height: 160,
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#f5f5f5',
                          cursor: 'pointer'
                        }}
                        onClick={() => setPreviewImage(getFileUrl(attachment))}
                      >
                        <img
                          src={getFileUrl(attachment)}
                          alt={attachment.name}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          height: 160,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#f5f5f5'
                        }}
                      >
                        {getFileIcon(attachment.fileType)}
                      </div>
                    )
                  }
                  actions={[
                    <EditOutlined key="edit" onClick={() => handleEdit(attachment)} />,
                    <Popconfirm
                      key="delete"
                      title="确定删除此附件？"
                      onConfirm={() => handleDelete(attachment.id)}
                    >
                      <DeleteOutlined style={{ color: '#ff4d4f' }} />
                    </Popconfirm>
                  ]}
                >
                  <Card.Meta
                    title={
                      <Space>
                        {attachment.name}
                        {!attachment.isActive && <Tag color="default">已禁用</Tag>}
                      </Space>
                    }
                    description={
                      <div>
                        {attachment.category && (
                          <Tag color="blue">{attachment.category}</Tag>
                        )}
                        {attachment.description && (
                          <p
                            style={{
                              marginTop: 8,
                              fontSize: 12,
                              color: '#999',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {attachment.description}
                          </p>
                        )}
                      </div>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>

      {/* 上传模态框 */}
      <Modal
        title="上传附件"
        open={uploadModalVisible}
        onOk={handleUpload}
        onCancel={() => setUploadModalVisible(false)}
        okText="上传"
        cancelText="取消"
      >
        <Form form={uploadForm} layout="vertical">
          <Form.Item
            name="file"
            label="选择文件"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            rules={[{ required: true, message: '请选择文件' }]}
          >
            <Upload
              beforeUpload={(file) => {
                if (file.size > 15 * 1024 * 1024) {
                  message.error(`文件不能超过 15MB（当前 ${(file.size / 1024 / 1024).toFixed(1)}MB），请压缩后再上传`);
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
              maxCount={1}
              accept="image/*,.pdf"
              listType="picture"
            >
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
            <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
              支持图片和 PDF，单个文件不超过 15MB
            </div>
          </Form.Item>

          <Form.Item
            name="name"
            label="附件名称"
            rules={[{ required: true, message: '请输入附件名称' }]}
          >
            <Input placeholder="如：中国入境卡填写指南" />
          </Form.Item>

          <Form.Item name="nameEn" label="附件名称(英文)">
            <Input placeholder="如：China Arrival Card Guide" />
          </Form.Item>

        </Form>
      </Modal>

      {/* 编辑模态框 */}
      <Modal
        title="编辑附件信息"
        open={editModalVisible}
        onOk={handleUpdate}
        onCancel={() => setEditModalVisible(false)}
        okText="保存"
        cancelText="取消"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="name"
            label="附件名称"
            rules={[{ required: true, message: '请输入附件名称' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="nameEn" label="附件名称(英文)">
            <Input />
          </Form.Item>

          <Form.Item name="sortOrder" label="排序">
            <Input type="number" />
          </Form.Item>

          <Form.Item name="isActive" label="启用状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 图片预览 */}
      <Image
        style={{ display: 'none' }}
        src={previewImage}
        preview={{
          visible: !!previewImage,
          src: previewImage,
          onVisibleChange: (visible) => {
            if (!visible) setPreviewImage(null);
          }
        }}
      />
    </div>
  );
};

export default NoticeAttachments;
